import { type ModuleId, hasModule } from '@/lib/modules';
import type { MeResponse } from '@sklad-sync/shared';
import { Link } from 'react-router-dom';

type Props = {
  user: MeResponse;
};

type Tool = {
  slug: string;
  name: string;
  description: string;
  icon: JSX.Element;
  status?: 'live' | 'soon';
  kind?: 'tool' | 'data';
  // Modul, který musí mít uživatel pro přístup. Když chybí v user.modules,
  // dlaždice se v landingu nezobrazí.
  module: ModuleId;
};

const tools: Tool[] = [
  {
    slug: 'analyza-historie-prodeje',
    module: 'sales_history',
    name: 'Analýza historie prodeje',
    description:
      'Prodeje po měsících — TOP produkty, Pareto (80 % tržeb), stockouty a sezónnost jako podklad pro řízení výroby.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 3v18h18" />
        <path d="M7 16v-5" />
        <path d="M12 16V7" />
        <path d="M17 16v-8" />
      </svg>
    ),
  },
  {
    slug: 'vyroba',
    module: 'vyroba',
    name: 'Výroba',
    description:
      'Zakázky do výroby s termíny — naskladnění do výroby, vyskladnění hotových kusů a naskladnění na sklad. Běží i samostatně na vyroba.v-martsystem.cz.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 21h18" />
        <path d="M5 21V10l5 3V10l5 3V7l4 2v12" />
      </svg>
    ),
  },
  {
    slug: 'sklad-sync',
    module: 'sklad_sync',
    name: 'Sklad-Sync',
    description:
      'Denní porovnání skladových stavů mezi Shoptetem, Brani skladem a výrobním systémem. Najde nesrovnalosti a navrhne akce.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 7l9-4 9 4-9 4-9-4z" />
        <path d="M3 7v10l9 4 9-4V7" />
        <path d="M12 11v10" />
      </svg>
    ),
  },
  {
    slug: 'propojeni-produktu',
    module: 'propojeni',
    name: 'Propojení produktů',
    description:
      'Skupiny produktů, které se propojí na detailu na e-shopu. Zákazník vidí selektor s ostatními variantami (barvy, velikosti, styly).',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
  },
  {
    slug: 'produktove-sady',
    module: 'sady',
    name: 'Produktové sady',
    description:
      'Výhodné/dárkové sady s variantami. Zákazník vybírá velikost u každé položky zvlášť, do košíku jdou jednotlivá SKU.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
  },
  {
    slug: 'analytics',
    module: 'analytics',
    name: 'V-Mart Analytics',
    description:
      'Přehled z GA4: relace, nákupy, konverze, nákupní funnel a zdroje. Vlastní dashboard nad daty z www.v-mart.cz.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="3" y1="20" x2="21" y2="20" />
      </svg>
    ),
  },
  {
    slug: 'odpocty',
    module: 'odpocty',
    name: 'Odpočty času',
    description:
      'Countdown GIF do emailů (à la MailTimers). Vytvoř odpočet s termíny, vlož veřejnou URL jako obrázek a v emailu běží živý odpočet.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="13" r="8" />
        <path d="M12 13V9" />
        <path d="M9 2h6" />
        <path d="M12 13l3 2" />
      </svg>
    ),
  },
  {
    slug: 'upsell-cross-sell',
    module: 'upsell',
    name: 'Upsell & Cross-sell',
    description:
      'Bannery a nabídky vykreslované na e-shopu. Spravuj kampaně, slevy a doporučení, které zvyšují hodnotu objednávky.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 3h2l2.4 12.5a2 2 0 0 0 2 1.5h9.4a2 2 0 0 0 2-1.6L22 7H6" />
        <circle cx="9" cy="20" r="1.5" />
        <circle cx="18" cy="20" r="1.5" />
        <path d="M13 11v-4M11 9h4" />
      </svg>
    ),
  },
  {
    slug: 'automatizace-objednavek',
    module: 'automatizace_objednavek',
    name: 'Automatizace objednávek',
    description:
      'Přehled pravidel, podle kterých se u CZ a SK e-shopu automaticky přepínají stavy objednávek — zařazení do termínu, na zakázku, expedice.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 3v3m0 12v3m9-9h-3M6 12H3" />
        <path d="M18.36 5.64l-2.12 2.12M7.76 16.24l-2.12 2.12M18.36 18.36l-2.12-2.12M7.76 7.76 5.64 5.64" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    ),
  },
  {
    slug: 'rozdily-skladu',
    module: 'rozdily_skladu',
    name: 'Rozdíly skladů',
    description:
      'Kontrola nesrovnalostí mezi e-shopy a skladem — zatím CZ vs. SK skladovost podle nahraných exportů.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M9 3v18M3 9h18M3 15h18" />
      </svg>
    ),
  },
  {
    slug: 'produkt-tydne',
    module: 'produkt_tydne',
    name: 'Produkt týdne',
    description:
      'Týdenní akce s vybranými produkty — sleva, vyšší priorita a štítek na e-shopu. Naplánuj kampaně postupně dopředu.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 2l2.39 4.84L20 7.5l-4 3.9.94 5.5L12 14.5l-4.94 2.4L8 11.4l-4-3.9 5.61-.66L12 2z" />
      </svg>
    ),
  },
  {
    slug: 'razeni-kategorii',
    module: 'razeni_kategorii',
    name: 'Řazení v kategoriích',
    description:
      'Pořadí produktů v kategoriích e-shopu přetažením myší — CZ i SK, uloží se jako priorita v Shoptetu.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M8 6h13M8 12h13M8 18h13M3 6l1.5-1.5L6 6M3 18l1.5 1.5L6 18M4.5 4.5v15" />
      </svg>
    ),
  },
  {
    slug: 'skladove-zasoby',
    module: 'skladove_zasoby',
    name: 'Skladové zásoby',
    description:
      'Hodnota skladu v prodejních cenách bez DPH po kategoriích — a které produkty v ní leží nejvíc.',
    status: 'live',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 8l-9-5-9 5v8l9 5 9-5V8zM3 8l9 5 9-5M12 13v8" />
      </svg>
    ),
  },
];

const dataSources: Tool[] = [
  {
    slug: 'databaze-produktu',
    module: 'products_db',
    name: 'Databáze produktů',
    description:
      'Sdílený katalog produktů ze Zboží.cz feedu. Aktualizuje se denně, slouží jako zdroj pro všechny tools.',
    status: 'live',
    kind: 'data',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M3 5v6c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
        <path d="M3 11v6c0 1.66 4.03 3 9 3s9-1.34 9-3v-6" />
      </svg>
    ),
  },
  {
    slug: 'databaze-objednavek',
    module: 'orders_db',
    name: 'Databáze objednávek',
    description:
      'Objednávky ze Shoptet REST API. Cron sync každých 15 minut, slouží jako přesný zdroj prodejů pro Analytics a další tools.',
    status: 'live',
    kind: 'data',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <path d="M9 12h6M9 16h6" />
      </svg>
    ),
  },
];

export function ToolsLandingPage({ user }: Props) {
  // Filtruj dlaždice podle uživatelského přístupu — co nemá v modules, neuvidí.
  const mods = user.modules ?? [];
  const visibleTools = tools.filter((t) => hasModule(mods, t.module));
  const visibleData = dataSources.filter((t) => hasModule(mods, t.module));
  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Nástroje V-Mart</h1>
        <p className="text-sm text-text-secondary mt-0.5">
          Vítej, {user.name}. Vyber, na čem chceš pracovat.
        </p>
      </div>

      {visibleTools.length > 0 && (
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
          Nástroje
        </h2>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        {visibleTools.map((t) => (
          <ToolTile key={t.slug} tool={t} />
        ))}

        {/* Placeholder pro chystaný tool, ať vidíš, kam se vlistne */}
        <div className="bg-surface rounded-xl p-5 shadow-card opacity-60 border-2 border-dashed border-border">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-12 h-12 bg-bg border border-border rounded-lg flex items-center justify-center text-text-muted flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-text-secondary">Připravujeme...</h2>
              <p className="text-sm text-text-muted mt-1 leading-relaxed">
                Tady přibyde další nástroj (propojování produktů, …).
              </p>
            </div>
          </div>
        </div>
      </div>

      {visibleData.length > 0 && (
        <>
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
            Sdílená data
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {visibleData.map((t) => (
              <ToolTile key={t.slug} tool={t} />
            ))}
          </div>
        </>
      )}

      {visibleTools.length === 0 && visibleData.length === 0 && (
        <div className="bg-surface border border-border rounded-xl p-10 text-center">
          <div className="text-text-muted text-sm">Zatím nemáš přístup k žádnému nástroji.</div>
          <div className="text-text-muted text-xs mt-1">Požádej správce o přidělení modulů.</div>
        </div>
      )}
    </>
  );
}

function ToolTile({ tool: t }: { tool: Tool }) {
  const iconBg = t.kind === 'data' ? 'bg-success' : 'bg-primary';
  return (
    <Link
      to={`/${t.slug}`}
      className="bg-surface rounded-xl p-5 shadow-card hover:shadow-card-hover transition-all cursor-pointer block"
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className={`w-12 h-12 ${iconBg} rounded-lg flex items-center justify-center text-white flex-shrink-0`}
        >
          {t.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-text">{t.name}</h2>
            {t.status === 'soon' && (
              <span className="text-[10px] font-semibold text-warning bg-warning-light px-1.5 py-0.5 rounded">
                Brzy
              </span>
            )}
          </div>
          <p className="text-sm text-text-secondary mt-1 leading-relaxed">{t.description}</p>
        </div>
      </div>
      <div className="text-sm font-semibold text-primary mt-2">Otevřít →</div>
    </Link>
  );
}
